import { Button, Grid, Tabs } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import {
    DeleteVariantConfiguratorDocument,
    type DeleteVariantConfiguratorMutation,
    type DeleteVariantConfiguratorMutationVariables,
} from '../../../api/mutations/deleteVariantConfigurator';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useDeletionConfirm from './useDeletionConfirm';
import { HeaderTabs } from './utils';
import { useVariantConfigurator } from './VariantConfiguratorContext';

type VariantConfiguratorDetailParams = {
    modelConfiguratorId: string;
    variantConfiguratorId: string;
};

type VariantConfiguratorDetailsInnerPageProps = {
    children: React.ReactNode;
    footer: React.ReactNode[];
    activeTab: HeaderTabs;
    replaceTab: (value: HeaderTabs) => void;
    subTitle?: React.ReactNode;
};

const VariantConfiguratorLayout = ({
    children,
    footer,
    subTitle,
    activeTab,
    replaceTab,
}: VariantConfiguratorDetailsInnerPageProps) => {
    const { t } = useTranslation(['variantConfiguratorDetails']);
    const { variantConfigurator } = useVariantConfigurator();
    const screens = Grid.useBreakpoint();

    const { modelConfiguratorId, variantConfiguratorId } = useParams<VariantConfiguratorDetailParams>();

    const navigate = useNavigate();

    // Delete confirmation
    const deleteConfirmation = useDeletionConfirm<
        DeleteVariantConfiguratorMutation,
        DeleteVariantConfiguratorMutationVariables
    >('variantConfiguratorDetails', 'deleteModals.variant', 'messages.variant');

    const deleteVariantConfigurator = useCallback(() => {
        deleteConfirmation(
            {
                mutation: DeleteVariantConfiguratorDocument,
                variables: { id: variantConfiguratorId },
            },
            () => {
                // then go back to list
                navigate(`/admin/configurators/${modelConfiguratorId}`);
            }
        );
    }, [deleteConfirmation, modelConfiguratorId, navigate, variantConfiguratorId]);

    const hasDeletePermission = useMemo(
        () => hasPermissions(variantConfigurator.permissions, [permissionKind.deleteConfigurator]),
        [variantConfigurator.permissions]
    );

    const extra = hasDeletePermission && (
        <Button onClick={deleteVariantConfigurator} danger>
            {t('variantConfiguratorDetails:actions.variant.delete')}
        </Button>
    );

    return (
        <ConsolePageWithHeader
            extra={extra}
            footer={footer}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={replaceTab}>
                        <Tabs.TabPane key={HeaderTabs.Details} tab={t('variantConfiguratorDetails:tabs.details')} />
                        <Tabs.TabPane key={HeaderTabs.Colors} tab={t('variantConfiguratorDetails:tabs.colors')} />
                        <Tabs.TabPane key={HeaderTabs.Trims} tab={t('variantConfiguratorDetails:tabs.trims')} />
                        <Tabs.TabPane key={HeaderTabs.Packages} tab={t('variantConfiguratorDetails:tabs.packages')} />
                        <Tabs.TabPane key={HeaderTabs.Options} tab={t('variantConfiguratorDetails:tabs.options')} />
                    </Tabs>
                ),
            }}
            onBack={() => navigate(-1)}
            subTitle={subTitle}
            title={
                screens.md
                    ? t('variantConfiguratorDetails:title', { variantName: variantConfigurator.displayName })
                    : variantConfigurator.displayName
            }
        >
            {children}
        </ConsolePageWithHeader>
    );
};

export default VariantConfiguratorLayout;
